import { AlphartisLogo } from "@/components/icons";
import { Loader, LoaderVariant } from "@/components/ui/loader";
import { MotionConfig, motion, Variants } from "framer-motion";
import { RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";

const CONTAINER_VARIANTS: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

const ITEM_VARIANTS: Variants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

export function MaintenanceView() {
  const { t } = useTranslation("views");

  return (
    <MotionConfig reducedMotion="user">
      <main className="bg-background relative flex min-h-dvh items-center justify-center overflow-hidden p-4 sm:p-8">
        <div
          aria-hidden="true"
          className="bg-primary/10 pointer-events-none absolute top-1/2 left-1/2 size-[28rem] -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl sm:size-[40rem]"
        />

        <div className="text-foreground absolute top-6 left-6 flex items-center gap-2 font-semibold">
          <AlphartisLogo className="text-primary size-6" />
          <span>Alphartis</span>
        </div>

        <motion.div
          className="relative z-10 mx-auto flex max-w-2xl flex-col items-center gap-6 text-center sm:gap-8"
          variants={CONTAINER_VARIANTS}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={ITEM_VARIANTS}>
            <Loader variant={LoaderVariant.Lift} animationClassName="h-40 w-52 sm:h-56 sm:w-72" aria-hidden="true" />
          </motion.div>

          <motion.span
            variants={ITEM_VARIANTS}
            className="border-primary/20 bg-primary/10 text-primary inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm font-medium"
          >
            <span className="relative flex size-2">
              <span className="bg-primary absolute inline-flex size-full animate-ping rounded-full opacity-75 motion-reduce:animate-none" />
              <span className="bg-primary relative inline-flex size-2 rounded-full" />
            </span>
            {t("Maintenance.Badge")}
          </motion.span>

          <motion.div variants={ITEM_VARIANTS} className="space-y-3">
            <h1 className="text-3xl font-bold tracking-tight text-balance sm:text-5xl">{t("Maintenance.Title")}</h1>
            <p className="text-primary text-lg font-semibold text-balance sm:text-2xl">{t("Maintenance.Message")}</p>
          </motion.div>

          <motion.p
            variants={ITEM_VARIANTS}
            className="text-muted-foreground max-w-xl text-base leading-relaxed text-balance sm:text-lg"
          >
            {t("Maintenance.Description")}
          </motion.p>

          <motion.p variants={ITEM_VARIANTS} className="text-muted-foreground inline-flex items-center gap-2 text-sm">
            <RefreshCw className="size-4 shrink-0 [animation-duration:3s] motion-safe:animate-spin" />
            {t("Maintenance.AutoContinue")}
          </motion.p>
        </motion.div>
      </main>
    </MotionConfig>
  );
}
